import { isAfter, isBefore, isSameDay } from 'date-fns';
import { Dispatch, FC, SetStateAction, useCallback } from 'react';
import {
  dateIsAfter,
  dateIsBefore,
  dateIsBetween,
  DateRangeType,
} from '@lib/utils.ts';

interface DaysProps {
  asSingle?: boolean;
  calendarData: {
    previous: Date[];
    current: Date[];
    next: Date[];
  };
  startDate: Date | null;
  endDate: Date | null;
  hoverDate: Date | null;
  setHoverDate: Dispatch<SetStateAction<Date | null>>;
  onDateClick: (date: Date) => void;
  minDate?: Date | null;
  maxDate?: Date | null;
  disabledDates?: DateRangeType[] | null;
  onClickPreviousDays: (day: Date) => void;
  onClickNextDays: (day: Date) => void;
}
export const Days: FC<DaysProps> = ({
  asSingle,
  calendarData,
  endDate,
  startDate,
  hoverDate,
  setHoverDate,
  minDate,
  maxDate,
  disabledDates,
  onDateClick,
  onClickNextDays,
  onClickPreviousDays,
}) => {
  const isStart = (day: Date) => startDate && isSameDay(day, startDate);
  const isEnd = (day: Date) => endDate && isSameDay(day, endDate);
  const isInRange = (day: Date) =>
    startDate && endDate && isAfter(day, startDate) && isBefore(day, endDate);
  const today = new Date();

  const isInHoverRange = (day: Date) => {
    if (!hoverDate) return false;
    if (startDate && !endDate) {
      return (
        (isAfter(day, startDate) && isBefore(day, hoverDate)) ||
        (isBefore(day, startDate) && isAfter(day, hoverDate))
      );
    }

    return false;
  };

  const isDateTooEarly = useCallback(
    (day: Date) => {
      if (!minDate) return false;

      return dateIsBefore(day, minDate, 'date');
    },
    [minDate]
  );

  const isDateTooLate = useCallback(
    (day: Date) => {
      if (!maxDate) return false;

      return dateIsAfter(day, maxDate, 'date');
    },
    [maxDate]
  );

  const isDateDisabled = useCallback(
    (day: Date) => {
      if (isDateTooEarly(day) || isDateTooLate(day)) {
        return true;
      }

      if (
        !disabledDates ||
        (Array.isArray(disabledDates) && !disabledDates.length)
      ) {
        return false;
      }

      let matchingCount = 0;

      disabledDates?.forEach((dateRange) => {
        if (
          dateRange.startDate &&
          dateRange.endDate &&
          dateIsBetween(day, dateRange.startDate, dateRange.endDate, 'date', {
            start: true,
            end: true,
          })
        ) {
          matchingCount++;
        }
      });

      return matchingCount > 0;
    },
    [isDateTooEarly, isDateTooLate, disabledDates]
  );

  const handleClickDay = useCallback(
    (day: Date, type: 'previous' | 'current' | 'next') => {
      function continueClick() {
        if (type === 'previous') {
          onClickPreviousDays(day);
        }

        if (type === 'current') {
          onDateClick(day);
        }

        if (type === 'next') {
          onClickNextDays(day);
        }
      }

      if (disabledDates?.length) {
        continueClick();
      } else {
        continueClick();
      }
    },
    [disabledDates?.length, onDateClick, onClickNextDays, onClickPreviousDays]
  );

  return (
    <div>
      <div className="grid grid-cols-7 text-xs text-[#868685] mb-5">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
          <div key={d} className="text-center">
            {d}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-y-1 text-center text-sm mb-4 mx-4">
        {calendarData.previous.map((item, index) => (
          <button
            key={index}
            className={`w-12 h-12 flex items-center justify-center text-sm text-[#9CA3AF]`}
            disabled={isDateDisabled(item)}
            type="button"
            onClick={() => handleClickDay(item, 'previous')}
          >
            {item.getDate()}
          </button>
        ))}
        {calendarData.current.map((day) => {
          const isStartDate = isStart(day);
          const isEndDate = isEnd(day);
          const inRange = isInRange(day);
          const inHoverRange = isInHoverRange(day);
          const isToday = isSameDay(day, today);

          let classes = 'w-12 h-12 flex items-center justify-center text-sm';

          if (asSingle && isStartDate) {
            classes += ' bg-[#3B82F6] font-semibold text-white rounded-full';
          } else if (isStartDate || isEndDate) {
            classes += ' bg-[#3B82F6] font-semibold text-white';
            if (isStartDate && !isEndDate) classes += ' rounded-l-full';
            if (isEndDate && !isStartDate) classes += ' rounded-r-full';
            if (isStartDate && isEndDate) classes += ' rounded-full';
          } else if ((inRange || inHoverRange) && !asSingle) {
            classes += ' bg-[#DBEAFE] text-[#262626]';
          } else {
            if (isToday) {
              classes += ' text-[#3B82F6]';
            }
            classes += ' hover:bg-white/20 rounded-full';
          }

          return (
            <button
              key={day.toISOString()}
              className={classes}
              onClick={() => handleClickDay(day, 'current')}
              onMouseEnter={() => setHoverDate(day)}
            >
              {day.getDate()}
            </button>
          );
        })}
        {calendarData.next.map((item, index) => (
          <button
            key={index}
            className={`w-12 h-12 flex items-center justify-center text-sm text-[#9CA3AF]`}
            disabled={isDateDisabled(item)}
            type="button"
            onClick={() => handleClickDay(item, 'previous')}
          >
            {item.getDate()}
          </button>
        ))}
      </div>
    </div>
  );
};
